import styled, { css } from 'styled-components'
// @ts-ignore
import { sys, basicColor } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { SystemTheme } from 'hooks/useUiState/types'
import { AUTO_ATTR_KEY, AutomationId } from '../../../utils/automationUtil'

export const StyledHeader = styled.div.attrs({ [AUTO_ATTR_KEY]: AutomationId.widget.header })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 6px;
`

interface StyledContainerProps {
  displayTheme?: Theme
}

export const StyledContainer = styled.div<StyledContainerProps>`
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ displayTheme }) => (displayTheme === 'dark' ? '#1E2224' : '#ffffff')};
  overflow: hidden;
  border-radius: 2px;
  box-shadow: ${({ displayTheme }) =>
    displayTheme === 'dark'
      ? 'none'
      : '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)'};
`

interface ScrollBarStylesProps {
  displayTheme?: Theme
  scrollableX?: boolean
  scrollableY?: boolean
}

const ScrollBarStyles = css<ScrollBarStylesProps>`
  overflow-x: ${({ scrollableX = true }) => (scrollableX ? 'auto' : 'hidden')};
  overflow-y: ${({ scrollableY = true }) => (scrollableY ? 'auto' : 'hidden')};
  scrollbar-color: ${({ displayTheme }) =>
      displayTheme === 'dark' ? basicColor.GRAY_800 : '#8F9BA3'}
    transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-corner {
    display: none;
  }
  ::-webkit-scrollbar-thumb:horizontal {
    background-color: ${({ displayTheme }) =>
      displayTheme === 'dark' ? basicColor.GRAY_800 : '#8F9BA3'};
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:horizontal:hover {
    background-color: ${({ displayTheme }) =>
      displayTheme === 'dark' ? basicColor.GRAY_700 : basicColor.GRAY_800};
  }
  ::-webkit-scrollbar-thumb:vertical {
    background-color: ${({ displayTheme }) =>
      displayTheme === 'dark' ? basicColor.GRAY_800 : '#8F9BA3'};
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:vertical:hover {
    background-color: ${({ displayTheme }) =>
      displayTheme === 'dark' ? basicColor.GRAY_700 : basicColor.GRAY_800};
  }
`

interface StyledContentProps {
  displayTheme?: Theme
  scrollableX?: boolean
  scrollableY?: boolean
}

export const StyledContent = styled.div<StyledContentProps>`
  flex: 1;
  width: 100%;
  padding-right: 15px;
  overflow: hidden;
  ${({ scrollableX = true, scrollableY = true }) => (scrollableX || scrollableY) && ScrollBarStyles}
`

export const ActionButtonsContainer = styled.div`
  margin-right: -6px;
  display: none;

  ${StyledContainer}:hover & {
    display: unset;
  }
`

export const StyledInfoIconContainer = styled.div`
  margin: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export const StyledIconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export const HeaderContainer = styled.div`
  flex: 1 1 54px;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 10px 0;
  font-weight: 600;
  font-size: 20px;
  font-family: var(--large-font-family);
  line-height: 28px;
`

export const Divider = styled.hr<{ $mode: Theme }>`
  width: 100%;
  margin: 4px 0;
  border-width: 0;
  border-bottom: 1px solid ${({ $mode }) => sys[$mode].common.divider[100]};
`

export const StyledInfoContentContainer = styled.div<{ $theme: SystemTheme }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0px 16px;
  align-items: start;
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.medium};
  height: 100%;
  max-width: 400px;
  max-height: 400px;

  ${Divider} {
    grid-column: 1 / -1;
  }
`

export const Label = styled.div<{ $theme: SystemTheme }>`
  height: fit-content;
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.medium};
  text-align: left;
`

export const Value = styled.div<{ $theme: SystemTheme }>`
  height: fit-content;
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  text-align: left;
`

export const LinkValue = styled.a<{ $theme: SystemTheme; $mode: Theme }>`
  font-size: ${({ $theme }) => $theme.typescale[12].size};
  text-decoration: underline;
  line-height: ${({ $theme }) => $theme.typescale[12].lineHeight};
  font-weight: ${({ $theme }) => $theme.fontWeight.semibold};
  color: ${({ $mode }) => sys[$mode].interaction.primary.default};
  cursor: pointer;
  &:hover {
    color: ${({ $mode }) => sys[$mode].interaction.primary.hover};
  }
  &:active {
    color: ${({ $mode }) => sys[$mode].interaction.primary.pressed};
  }
`

export const ValueList = styled.ul`
  padding: 0;
  margin: 0;
`

export const ValueListItem = styled.li`
  list-style: none;
  padding: 0;
  margin: 0;
`

export const ButtonHoverWrapper = styled.div`
  display: inline;
  pointer-events: auto;
`
